"use client";

import React from "react";
import { useController, Control, FieldPath, FieldValues } from "react-hook-form";
import {
  FormControl,
  FormDescription,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { cn } from "../../../lib/src/utils";

interface SelectOption {
  value: string;
  label: string;
}

interface SelectInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
  name: TName;
  control: Control<TFieldValues>;
  label: string;
  placeholder?: string;
  description?: string;
  helperText?: string;
  errorText?: string;
  options: SelectOption[];
  className?: string;
  required?: boolean;
  disabled?: boolean;
}

export function SelectInput<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  name,
  control,
  label,
  placeholder,
  description,
  helperText,
  errorText,
  options,
  className,
  required = false,
  disabled = false,
}: SelectInputProps<TFieldValues, TName>) {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  const hasError = error || errorText;

  return (
    <FormItem className={cn("space-y-2", className)}>
      <FormLabel className={cn(required && "after:content-['*'] after:ml-0.5 after:text-red-500")}>
        {label}
      </FormLabel>
      <Select onValueChange={field.onChange} value={field.value} disabled={disabled}>
        <FormControl>
          <SelectTrigger className={cn(
            hasError && "border-destructive focus-visible:ring-destructive",
            disabled && "opacity-50 cursor-not-allowed"
          )}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
        </FormControl>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {description && <FormDescription className="text-sm text-muted-foreground">{description}</FormDescription>}
      {helperText && !hasError && <FormDescription className="text-sm text-muted-foreground">{helperText}</FormDescription>}
      {hasError && <FormMessage>{errorText || error?.message}</FormMessage>}
    </FormItem>
  );
}
